import { useState, useEffect } from 'react';
import { STORAGE_KEYS } from '@/lib/storage-keys';

export type ConsentStatus = 'accepted' | 'declined' | 'pending';

function getConsentStatus(): ConsentStatus {
  try {
    // Check localStorage for accepted
    const stored = localStorage.getItem(STORAGE_KEYS.CONSENT.key);
    if (stored === 'accepted') return 'accepted';

    // Check sessionStorage for declined (only for this tab/session)
    const sessionStored = sessionStorage.getItem(STORAGE_KEYS.CONSENT_SESSION.key);
    if (sessionStored === 'declined') return 'declined';

    return 'pending';
  } catch {
    return 'pending';
  }
}

export function useConsent() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(getConsentStatus);

  // Listen for storage changes (when banner updates consent in another component)
  useEffect(() => {
    const handleStorageChange = () => {
      setConsentStatus(getConsentStatus());
    };

    // Listen for custom event (for same-tab updates)
    window.addEventListener('consent-changed', handleStorageChange);
    // Listen for storage event (for cross-tab updates)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('consent-changed', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const acceptConsent = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.CONSENT.key, 'accepted');
      sessionStorage.removeItem(STORAGE_KEYS.CONSENT_SESSION.key); // Clear declined from session
      setConsentStatus('accepted');
      // Dispatch custom event for same-tab updates
      window.dispatchEvent(new Event('consent-changed'));
    } catch (error) {
      console.error('Failed to save consent:', error);
    }
  };

  const declineConsent = () => {
    try {
      // Store declined in sessionStorage only (doesn't persist across sessions)
      sessionStorage.setItem(STORAGE_KEYS.CONSENT_SESSION.key, 'declined');
      setConsentStatus('declined');
      // Dispatch custom event for same-tab updates
      window.dispatchEvent(new Event('consent-changed'));
    } catch (error) {
      console.error('Failed to save consent:', error);
    }
  };

  const revokeConsent = () => {
    try {
      // Clear all app data
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('kctools-') || key === 'theme') {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.removeItem(STORAGE_KEYS.CONSENT_SESSION.key);
      setConsentStatus('pending');
      // Dispatch custom event for same-tab updates
      window.dispatchEvent(new Event('consent-changed'));
    } catch (error) {
      console.error('Failed to revoke consent:', error);
    }
  };

  const hasConsented = consentStatus === 'accepted';
  const hasDeclined = consentStatus === 'declined';
  const isPending = consentStatus === 'pending';

  return {
    consentStatus,
    hasConsented,
    hasDeclined,
    isPending,
    acceptConsent,
    declineConsent,
    revokeConsent,
  };
}

// Utility function to check consent before localStorage operations
export function checkConsent(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CONSENT.key);
    return stored === 'accepted';
  } catch {
    return false;
  }
}
